<template>
  <div class="verificacao-cpf">
    <v-card class="verificacao-card" elevation="4">
      <div class="card-header">
        <h1 class="title">Atendimento Híbrido</h1>
        <span v-if="nomeClinica" class="clinic-name">{{ nomeClinica }}</span>
      </div>

      <v-divider />

      <div class="card-body">
        <div class="title-text">Identificação do Paciente</div>
        <div class="subtitle-text">Para iniciar o atendimento, informe seu CPF abaixo</div>

        <v-form ref="form" class="form-wrapper">
          <v-text-field
            v-model="cpf"
            label="CPF"
            placeholder="000.000.000-00"
            class="cpf-field"
            outlined
            dense
            prepend-inner-icon="mdi-card-account-details-outline"
            :loading="loadingCpf"
            :disabled="loadingCpf"
            v-mask="'###.###.###-##'"
            :rules="[
              () => $v.cpf.required || 'Campo obrigatório',
              () => $v.cpf.minLength || 'CPF inválido',
              v => validaCpf(v) || 'CPF inválido'
            ]"
          />

          <v-btn
            @click="verificaCpf"
            block
            large
            color="primary"
            class="buscar-btn"
            :loading="loadingCpf"
            :disabled="loadingCpf"
          >
            <v-icon left>mdi-magnify</v-icon>
            Buscar
          </v-btn>

          <v-alert
            v-if="showAnamneseError"
            type="error"
            dense
            outlined
            class="mt-4"
            border="left"
          >
            Anamnese precisa ser finalizada antes do atendimento ser iniciado.
          </v-alert>
        </v-form>
      </div>
    </v-card>

    <ConfirmDialog
      v-if="openBypassFichaClinicaDialog"
      v-model="openBypassFichaClinicaDialog"
      title="Atendimento encontrado"
      text="Identificamos que você já preencheu a ficha clínica deste atendimento, deseja ir direto para a fila de atendimento?"
      cancelButtonText="Revisar dados"
      confirmButtonText="Pular para o atendimento"
      confirmButtonColor="grey darken-1"
      @on-confirm="entrarNaFila"
      @on-cancel="preencherFichaClinica(aso)"
    />
  </div>
</template>
<script>
import atendimentoHibridoService from '@/services/atendimentoHibrido'
import authenticationService from '@/services/authentication'
import { validationMixin } from 'vuelidate'
import { required, minLength } from 'vuelidate/lib/validators'
import validaCpf from '@/utils/validaCpf'
import { extractOnlyNumbers } from '@/utils/formatter'
import { mapActions, mapMutations, mapState } from 'vuex'
import { SET_CUSTOMIZACAO_HIBRIDO } from '@/store/modules/app'
import ConfirmDialog from '@/core/components/ConfirmDialog'

const ID_WORKSPACE_SOLIDES = 38
const ID_WORKSPACE_LIMER = 1

const logosPorWorkspace = {
  [ID_WORKSPACE_SOLIDES]: require('@/assets/logo-solides.png'),
  [ID_WORKSPACE_LIMER]: require('@/assets/plataforma-limer-inverse.png'),
}

const LOGO_FALLBACK = require('@/assets/plataforma-limer-inverse.png')

export default {
  mixins: [validationMixin],
  components: {
    ConfirmDialog
  },
  data() {
    return {
      cpf: '',
      loadingCpf: false,
      openBypassFichaClinicaDialog: false,
      idAsoMeetingRoom: null,
      aso: null,
      showAnamneseError: false
    }
  },
  validations: {
    cpf: {
      required,
      minLength: minLength(14)
    }
  },
  computed: {
    ...mapState('app', ['nomeClinica'])
  },
  created() {
    this.setCustomizacaoHibrido({})
  },
  methods: {
    ...mapActions('fichaClinica', ['preparaFichaClinica']),
    ...mapMutations('app', { setCustomizacaoHibrido: SET_CUSTOMIZACAO_HIBRIDO }),
    validaCpf,
    async verificaCpf() {
      if (this.$refs.form.validate()) {
        try {
          this.showAnamneseError = false
          this.loadingCpf = true
          const { data } = await atendimentoHibridoService.validateCpf({
            cpf: extractOnlyNumbers(this.cpf),
            token: this.$route.params.idClinica
          })
          authenticationService.setToken(data.userToken)
          const { urlLogo, colorTheme } = data.customizacao || {}
          const logoFinal = logosPorWorkspace[data.idWorkspace] || urlLogo || LOGO_FALLBACK
          this.setCustomizacaoHibrido({ urlLogo: logoFinal, colorTheme })

          this.$showSuccessMessage(data.message)
          if (data.idAsoMeetingRoom) {
            this.idAsoMeetingRoom = data.idAsoMeetingRoom
            this.aso = { idAso: data.idAso, tipo: data.tipo }
            this.openBypassFichaClinicaDialog = true
          } else {
            this.preencherFichaClinica({ idAso: data.idAso, tipo: data.tipo })
          }
        } catch (error) {
          console.log(error)
          if (error.response.data.anamneseError) {
            this.showAnamneseError = true
          } else {
            this.$showErrorMessage(error.response.data.message)
          }
        } finally {
          this.loadingCpf = false
        }
      }
    },
    async preencherFichaClinica(aso) {
      try {
        this.$showLoading()
        const success = await this.preparaFichaClinica(aso)
        if (success) this.$router.push({ name: 'ficha-clinica-atendimento' })
      } catch (error) {
        console.log(error)
      } finally {
        this.$hideLoading()
      }
    },
    async entrarNaFila() {
      try {
        this.$showLoading()
        const { data } = await atendimentoHibridoService.entrarNaFila(this.aso.idAso)
        this.$router.push({ name: 'fila-atendimento-hibrido', params: { id: data.id } })
      } catch (error) {
        console.log(error)
        this.$showErrorMessage(error.response.data.message)
      } finally {
        this.$hideLoading()
      }
    }
  }
}
</script>
<style scoped>
.verificacao-cpf {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  background: #f4f6f9;
  padding: 24px;
}

.verificacao-card {
  width: 100%;
  max-width: 440px;
  border-radius: 16px !important;
  overflow: hidden;
}

.card-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--v-primary-base);
  padding: 28px 24px;
  gap: 4px;
}

.logo {
  height: 52px;
  object-fit: contain;
  filter: brightness(0) invert(1);
}

.card-body {
  padding: 32px 36px 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.title-text {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a2e;
  text-align: center;
  margin-bottom: 6px;
}

.subtitle-text {
  font-size: 0.875rem;
  color: #6b7280;
  text-align: center;
  margin-bottom: 28px;
}

.form-wrapper {
  width: 100%;
}

.cpf-field {
  width: 100%;
  margin-bottom: 4px;
}

.buscar-btn {
  border-radius: 8px !important;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: none;
  font-size: 1rem;
}

.title {
  font-size: 1.25rem !important;
  font-weight: 700;
  color: #fff;
  text-align: center;
  line-height: 1.3;
}

.clinic-name {
  font-size: 0.875rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
  text-align: center;
  line-height: 1.4;
}
</style>
